import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

import { AnalyticsService } from '../../../services/analytics-wrapper/analytics.service';
import { BreakpointState } from '@angular/cdk/layout';
import { ButtonComponent } from '../../atoms/button/button.component';
import { EVENT_FLAG_VALUES } from '../../../constants/analytics.constant';
import { InputComponent } from '../../atoms/input/input.component';
import { LoginService } from '../../../services/login-service/login.service';
import { ROUTER_CONSTANTS } from '../../../constants/router.constants';
import { RenderLabelPipe } from '../../../pipes/render-label/render-label.pipe';
import { SEARCH_LAYOUT_VARIATION } from './search-layout-variations';
import { ViewportService } from '../../../services/viewport/viewport.service';
import { CommonUtility } from '../../../../common/utils/commonUtils';
import { ToasterService } from '../../../../shared/widgets/toaster/toaster.service';
import { COMMON_FORM_CONSTANTS } from '../../../../common/constants/global.constant';
import { AccountDetailService } from '../../../../modules/detail/services/account-detail.service';

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RenderLabelPipe,
    InputComponent,
    ReactiveFormsModule,
    ButtonComponent,
    NgTemplateOutlet,
  ],
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @Input() placeholder = '';
  @Input() label = '';
  @Input() className = '';
  @Input() buttonType: 'primary' | 'secondary' = 'primary';
  @Input() layout: string = SEARCH_LAYOUT_VARIATION.header;
  @Input() routerConstant: string = ROUTER_CONSTANTS.dashboard;
  @Output() searchClosed: any = new EventEmitter();
  @Input() isSearching = false;

  readonly layoutVariation = SEARCH_LAYOUT_VARIATION;
  ngUnsubscribe = new Subject<void>();
  isDesktop = true;
  clickHandler: any;
  isOpened = false;
  searchForm: FormGroup = this.formBuilder.group({
    searchInput: [this.activatedRoute.snapshot.queryParamMap.get('search')],
  });
  isSymphonyAccount = CommonUtility.isSymphonyAccount();

  constructor(
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private readonly _renderer: Renderer2,
    private readonly viewportService: ViewportService,
    private readonly cdr: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService,
    private readonly loginService: LoginService,
    private readonly toasterService: ToasterService,
    private readonly accountDetailService: AccountDetailService,
  ) {}

  /**
   * lifecycle method
   */
  ngOnInit(): void {
    this.viewportService.desktopObserver
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(200),
        distinctUntilChanged(),
      )
      .subscribe((result: BreakpointState) => {
        this.isDesktop = result.matches;
        this.cdr.detectChanges();
      });
    this.activatedRoute.queryParamMap
      ?.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((param) => {
        if (
          param.get('search') !== this.searchForm?.get('searchInput')?.value
        ) {
          this.searchForm?.get('searchInput')?.setValue(param.get('search'));
        }
      });
  }

  /**
   * Closes search overlay when user clicks outside of the search overlay
   */
  documentClick(): void {
    setTimeout(
      () =>
        (this.clickHandler = this._renderer.listen(
          document,
          'click',
          this._handleClick.bind(this),
        )),
    );
  }

  /**
   * Closes search overlay when user clicks outside of the search overlay
   * @param event : Browser event object
   * @returns boolean
   */
  _handleClick(event: any): boolean {
    // Do not close when
    // Case 1 : Clicking in search overlay itself
    // Case 2 : clicking on the header search control
    // Case 3 : Selecting the search by (Mat uses overlay on body level)
    // Case 4 : Clicking on clear button inside search term field
    // Case 5 : Clicking on overlay backdrop to close the search by dropdown
    if (
      event?.target &&
      (event.target.closest('#searchOverlay') ||
        event.target.getAttribute('id') === 'searchControl' ||
        event.target.closest('.clear') ||
        event.target.closest('div[id^="mat"]') ||
        event.target.className?.includes('cdk-overlay-backdrop'))
    ) {
      event.preventDefault();
      return false;
    }

    if (this.isOpened) {
      this.close();
    }

    return true;
  }

  /**
   * Opens the search overlay in mobile and tablet views
   */
  open(): void {
    // Renamed Searchbar placeholder value.
    const isCaseManagement = document.querySelector('app-case-detail');
    if (!isCaseManagement) {
      this.placeholder = this.placeholder.replace('case', 'profile');
    }
    this.documentClick();
    this.isOpened = true;
  }

  /**
   * Notifies Parent and closes Search overlay
   */
  close(): void {
    this.isOpened = false;

    this.searchClosed.emit(true);
    if (typeof this.clickHandler === 'function') {
      this.clickHandler();
    }
    this.clickHandler = null;
    this.cdr.detectChanges();
  }

  /**
   * search button click handler
   */
  onSearchHandler() {
    this.close();
    const searchValue = this.searchForm?.get('searchInput')?.value?.trim?.();
    this.analyticsService.createLinkClickTagObject(
      location.href.includes(`/${this.routerConstant}`)
        ? '#'
        : `/${this.routerConstant}`,
      `Search`,
      'Clicked on Search CTA',
      'Internal',
      {
        event_flag: EVENT_FLAG_VALUES.internal_site_search,
        on_site_search_term: searchValue,
      },
    );
    if (!searchValue) {
      return;
    }

    const isOnDashboard = this.router.url.includes(`/${this.routerConstant}`);
    const isCaseManagement = document.querySelector('app-case-detail');
    // Dashboard → redirect in SAME TAB
    if (isOnDashboard || isCaseManagement) {
      this.router.navigate([this.routerConstant], {
        queryParams: { search: searchValue },
      });
      return;
    }

    // Other pages → search first
    this.accountDetailService.getSearchTerm(searchValue).subscribe({
      next: (res) => {
        const results = res?.results ?? res.list ?? [];
        const totalCount = res?.total ?? res.totalCount ?? results.length;

        // CASE 2: No record found
        if (totalCount === 0) {
          this.showNoRecordFoundMessage();
          return;
        }

        // CASE 1: Exactly one record → OPEN IN NEW TAB
        if (totalCount === 1) {
          const account = results[0];
          const accountNumber = account?.CEP_Account_Number;

          if (!accountNumber) {
            this.showNoRecordFoundMessage();
            return;
          }

          if (this.router.url.includes(`/${accountNumber}`)) {
            this.showNoRecordFoundMessage();
            return;
          }

          const urlTree = this.router.createUrlTree([
            `/account/${accountNumber}`,
          ]);

          const url = this.router.serializeUrl(urlTree);
          window.open(url, '_blank');
          return;
        }

        // CASE 3: Multiple records → SAME TAB (search results page)
        this.router.navigate([this.routerConstant], {
          queryParams: { search: searchValue },
        });
      },
      error: () => {
        this.showNoRecordFoundMessage();
      },
    });
  }

  showNoRecordFoundMessage(): void {
    this.toasterService.error(
      COMMON_FORM_CONSTANTS.STRINGS.INFO_NO_RECORD_FOUND,
      4000,
    );
  }

  /**
   *
   * @param e $event from angular
   * added for accessibility tab-order
   * when focus move out from wrapper then it should close
   */
  searchWrapperFocusOut(e: any): void {
    if (!document.hasFocus()) {
      return;
    }
    if (
      e.relatedTarget &&
      !e.currentTarget.contains(e.relatedTarget) &&
      !(e.target?.getAttribute('data-automation') === 'searchBy-header')
    ) {
      this.close();
    }
  }

  /**
   * lifecycle method
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
